import {
  Alert,
  AlertProps,
  DropdownTagProps,
  FlagProps,
  IconButton,
  openDialog,
  Parameter,
  Parameters,
  Separator,
  Tooltip,
  TooltipProps,
} from 'platform/components';
import {
  Box,
  Heading,
  Hide,
  HStack,
  type HStackProps,
  Icon,
  Show,
  Space,
  Stack,
  Text,
  ThemeIconKey,
  useDevice,
  ValueByDevice,
  VStack,
} from 'platform/foundation';

import {and, or, reverse} from 'ramda';
import {isNilOrEmpty, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {DOT_CHARACTER, suffixTestId, TestIdProps} from 'shared/web';

import {useEmailConfig} from '../../hooks/useEmailConfig';
import {useKonzultaSmsAvailability} from '../../hooks/useKonzultaSmsAvailability';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {VehicleConditionEvaluationType} from '../../hooks/useVehicleConditionEvaluation';
import {InvalidEmailWarning} from '../InvalidEmailWarning/InvalidEmailWarning';
import {SendCustomerEmailForm} from '../SendCustomerEmailForm/SendCustomerEmailForm';
import {SendCustomerSmsForm} from '../SendCustomerSmsForm/SendCustomerSmsForm';
import {EntityHeaderActions} from './components/EntityHeaderActions';
import {EntityHeaderBackButton} from './components/EntityHeaderBackButton';
import {EntityHeaderControl, EntityHeaderControls} from './components/EntityHeaderControls';
import {EntityHeaderFlags} from './components/EntityHeaderFlags';
import {EntityHeaderImage} from './components/EntityHeaderImage';
import {SnippetProps} from './components/EntityHeaderSnippet';
import {EntityHeaderTitle} from './components/EntityHeaderTitle';
import {ListEditableTags} from './components/labels/ListEditableTags';
import {ParameterBlock} from './components/ParameterBlock';
import {SkeletonLoadingBox} from './components/SkeletonLoadingBox';
import {SnippetPanel} from './components/SnippetPanel';
import {VehicleConditionEvaluation} from './components/VehicleConditionEvaluation';
import type {DropdownPosition} from './types/DropdownPosition';
import type {EntityHeaderAction} from './types/EntityHeaderAction';
import type {EntityHeaderImageVariant} from './types/EntityHeaderImageVariant';
import type {HeaderPermissions} from './types/headerPermissions';
import type {VehicleHistoryControlConfig} from './types/VehicleHistoryControlConfig';

interface AdditionalParameterBlock {
  primaryParameter: string;
  secondaryParameter: string;
}

export interface EntityHeaderFlag extends Omit<FlagProps, 'size'> {
  tooltip?: TooltipProps;
}

export interface EntityHeaderDropdownFlag extends Omit<DropdownTagProps, 'size'> {
  tooltip?: TooltipProps;
}

export interface EntityHeaderProps extends TestIdProps {
  image?: string;
  imageRatio?: `${number}/${number}`;
  icon?: ThemeIconKey;
  flags?: EntityHeaderFlag[];
  dropdownFlags?: EntityHeaderDropdownFlag[];
  title?: string;
  parameters?: Parameter[];
  isEntityLoading?: boolean;
  isLoadingSnippets?: boolean;
  primaryParameter?: string;
  secondaryParameter?: string;
  tertiaryParameter?: string;
  additionalParameterBlocks?: AdditionalParameterBlock[];
  snippets?: SnippetProps[];
  actions?: EntityHeaderAction[];
  resourceId?: EntityResourceIds;
  recordId?: string;
  onImageClick?: () => void;
  controls?: EntityHeaderControl[];
  vehicleConditionEvaluation?: VehicleConditionEvaluationType;
  permissions?: HeaderPermissions;
  alerts?: AlertProps[];
  parkingRecordId?: string;
  authorizationProfileRecordId?: string;
  enableTeams?: boolean;
  isAuthorizationProfileEditable?: boolean;
  onAuthorizationProfileChange?: () => void;
  vehicleHistory?: VehicleHistoryControlConfig;
  imageVariant?: EntityHeaderImageVariant;
  variant?: 'detail' | 'create';
}

const MAX_VISIBLE_ACTIONS: ValueByDevice<number> = [2, 3, 4];
const ACTIONS_DROPDOWN_POSITION: ValueByDevice<DropdownPosition> = ['end', 'end', 'start'];
const ACTIONS_WRAP: HStackProps['wrap'] = [true, true, false];

function getHeaderLayout(params: {
  props: EntityHeaderProps;
  canReadBusinessCase: boolean;
  isMobileLayout: boolean;
  isCompactCreate: boolean;
}) {
  const {props, canReadBusinessCase, isMobileLayout, isCompactCreate} = params;

  const record =
    isNotNil(props.recordId) && isNotNil(props.resourceId)
      ? {recordId: props.recordId, resourceId: props.resourceId}
      : null;
  const controlsContext =
    isNotNilOrEmpty(props.controls) && isNotNil(props.controls) && isNotNil(props.recordId)
      ? {controls: props.controls, recordId: props.recordId}
      : null;

  const hasImageOrIcon = isNotNilOrEmpty(props.icon) || isNotNilOrEmpty(props.image);
  const hasOnlyIcon = isNotNilOrEmpty(props.icon) && isNilOrEmpty(props.image);

  return {
    isTitleInImage:
      isMobileLayout && hasImageOrIcon && (props.imageVariant === 'image' || hasOnlyIcon),
    hasAnyParameter:
      isNotNilOrEmpty(props.primaryParameter) ||
      isNotNilOrEmpty(props.secondaryParameter) ||
      isNotNilOrEmpty(props.tertiaryParameter),
    record,
    controlsContext,
    hasFlags: isNotNilOrEmpty(props.flags),
    hasFlagsRow:
      or(isNotNil(props.flags), and(isNotNil(props.resourceId), isNotNil(props.recordId))) ||
      isCompactCreate,
    hasParameters:
      isNotNilOrEmpty(props.parameters) ||
      Boolean(props.isEntityLoading) ||
      (canReadBusinessCase && isNotNil(props.vehicleConditionEvaluation)),
    hasControls: isNotNil(controlsContext),
    actions:
      isMobileLayout && !isCompactCreate ? reverse(props.actions ?? []) : (props.actions ?? []),
  };
}

export function EntityHeader(props: EntityHeaderProps) {
  const {isEnabled: isEmailConfigEnabled} = useEmailConfig();
  const {isSmsActionDisabled, smsTooltipLabel} = useKonzultaSmsAvailability();

  const device = useDevice();
  const isMobileLayout = device === 'mobile' || device === 'tablet';
  const isCompactCreate = props.variant === 'create' && isMobileLayout;

  const [canReadBusinessCase] = usePermissions({
    permissionKeys: ['businessCaseRead'],
  });

  const {
    isTitleInImage,
    hasAnyParameter,
    record,
    controlsContext,
    hasFlags,
    hasFlagsRow,
    hasParameters,
    hasControls,
    actions,
  } = getHeaderLayout({
    props,
    canReadBusinessCase,
    isMobileLayout,
    isCompactCreate,
  });

  const actionsNode = (
    <Show when={isNotNilOrEmpty(actions)}>
      <SkeletonLoadingBox isLoading={props.isEntityLoading} width={30} height={8}>
        <EntityHeaderActions
          actions={actions}
          data-testid={suffixTestId('entityHeader-actions', props)}
          dropdownPosition={ACTIONS_DROPDOWN_POSITION}
          maxVisibleActions={MAX_VISIBLE_ACTIONS}
          shouldStretchActions={!isCompactCreate}
          wrap={isCompactCreate ? false : ACTIONS_WRAP}
        />
      </SkeletonLoadingBox>
    </Show>
  );

  return (
    <>
      <Show when={!isCompactCreate}>
        <Hide onNotebook onDesktop>
          <EntityHeaderImage
            data-testid={suffixTestId('entityHeader-image', props)}
            icon={props.icon}
            image={props.image}
            onImageClick={props.onImageClick}
            isTitleLoading={props.isEntityLoading}
            ratio={props.imageRatio}
            title={props.title}
            imageVariant={props.imageVariant}
          />
        </Hide>
      </Show>

      <Box padding={[3, 3, 4]} backgroundColor="palettes.white.10.100">
        <VStack>
          <Show when={isCompactCreate}>
            <EntityHeaderBackButton data-testid={suffixTestId('entityHeader-backButton', props)}>
              {actionsNode}
            </EntityHeaderBackButton>
          </Show>
          <Stack direction={['column', 'column', 'row']} spacing={[3, 3, 4]} align="stretch">
            <Show onNotebook onDesktop>
              <EntityHeaderImage
                data-testid={suffixTestId('entityHeader-image', props)}
                icon={props.icon}
                image={props.image}
                onImageClick={props.onImageClick}
                ratio={props.imageRatio}
              />
            </Show>

            <VStack spacing={2}>
              <Show when={hasFlagsRow}>
                <HStack spacing={isCompactCreate ? 3 : 1} align="center" wrap>
                  <Show when={isCompactCreate}>
                    <HStack spacing={3} align="center">
                      <Show when={props.icon}>{(icon) => <Icon value={icon} size={10} />}</Show>
                      <EntityHeaderTitle
                        data-testid={suffixTestId('entityHeader-title', props)}
                        isLoading={props.isEntityLoading}
                        size={2}
                        title={props.title}
                      />
                    </HStack>
                  </Show>

                  <EntityHeaderFlags
                    data-testid={props['data-testid']}
                    dropdownFlags={props.dropdownFlags}
                    flags={props.flags}
                    isLoading={props.isEntityLoading}
                  />

                  <Show when={isNotNil(record) && hasFlags}>
                    <Text color="tertiary">{DOT_CHARACTER}</Text>
                  </Show>

                  <Show when={record}>
                    {(record) => (
                      <ListEditableTags
                        buttonVariant="secondary"
                        data-testid={suffixTestId('entityHeader-editable-tags', props)}
                        isLoading={props.isEntityLoading}
                        isTagAssignable={props.permissions?.tag?.isAssignable}
                        isTagCreatable={props.permissions?.tag?.isCreatable}
                        isTagDeletable={props.permissions?.tag?.isDeletable}
                        isTagReadable={props.permissions?.tag?.isReadable}
                        isTagUnassignable={props.permissions?.tag?.isUnassignable}
                        isTagUpdatable={props.permissions?.tag?.isUpdatable}
                        recordId={record.recordId}
                        resourceId={record.resourceId}
                        size="default"
                      />
                    )}
                  </Show>
                </HStack>
              </Show>

              <Show onMobile onTablet when={hasFlagsRow && (hasParameters || hasControls)}>
                <Separator spacing={1} />
              </Show>

              <Show when={!isCompactCreate && !isTitleInImage}>
                <EntityHeaderTitle
                  data-testid={suffixTestId('entityHeader-title', props)}
                  isLoading={props.isEntityLoading}
                  title={props.title}
                />
              </Show>

              <HStack spacing={1} wrap>
                <SkeletonLoadingBox isLoading={props.isEntityLoading} height={5} width={40}>
                  <Parameters
                    color="secondary"
                    data-testid={suffixTestId('entityHeader-parameters', props)}
                    parameters={props.parameters}
                    size="small"
                  />
                </SkeletonLoadingBox>

                <Show when={canReadBusinessCase}>
                  <SkeletonLoadingBox isLoading={props.isEntityLoading} height={5} width={40}>
                    <VehicleConditionEvaluation
                      color="secondary"
                      data-testid={suffixTestId('entityHeader-vehicle-condition', props)}
                      size="small"
                      vehicleCondition={props.vehicleConditionEvaluation}
                    />
                  </SkeletonLoadingBox>
                </Show>
              </HStack>

              <Show onMobile onTablet when={hasParameters && hasControls}>
                <Separator spacing={1} />
              </Show>

              <Show when={controlsContext}>
                {(controlsContext) => (
                  <EntityHeaderControls
                    authorizationProfileRecordId={props.authorizationProfileRecordId}
                    controls={controlsContext.controls}
                    data-testid={suffixTestId('entityHeader-controls', props)}
                    enableTeams={props.enableTeams}
                    isAuthorizationProfileEditable={props.isAuthorizationProfileEditable}
                    onAuthorizationProfileChange={props.onAuthorizationProfileChange}
                    parkingRecordId={props.parkingRecordId}
                    permissions={props.permissions}
                    recordId={controlsContext.recordId}
                    resourceId={props.resourceId}
                    vehicleHistory={props.vehicleHistory}
                  />
                )}
              </Show>

              <Show onMobile onTablet when={hasParameters || hasControls}>
                <Separator spacing={0} />
              </Show>
            </VStack>

            <Hide onMobile onTablet>
              <Space fillAvailable />
            </Hide>

            <Show when={props.snippets} onMobile onTablet>
              <SnippetPanel
                areSnippetsLoading={props.isLoadingSnippets}
                data-testid={suffixTestId('entityHeader-snippets', props)}
                resourceId={props.resourceId}
                snippets={props.snippets}
              />
            </Show>

            <Stack
              align="flex-end"
              direction={['column-reverse', 'column-reverse', 'column', 'column']}
              justify="space-between"
              spacing={2}
              width={['100%', '100%', 'auto']}
            >
              <Show when={!isCompactCreate}>{actionsNode}</Show>

              <HStack spacing={4}>
                <Show when={props.resourceId === 'VEHICLE' && props.additionalParameterBlocks}>
                  {props.additionalParameterBlocks?.map((block) => (
                    <ParameterBlock
                      isEmailConfigEnabled={isEmailConfigEnabled}
                      key={block.primaryParameter}
                      primaryParameter={block.primaryParameter}
                      recordId={props.recordId}
                      resourceId={props.resourceId}
                      secondaryParameter={block.secondaryParameter}
                    />
                  ))}
                </Show>

                <Show
                  when={
                    hasAnyParameter &&
                    props.resourceId === 'VEHICLE' &&
                    props.additionalParameterBlocks
                  }
                >
                  <Separator orientation="vertical" />
                </Show>

                <Show when={hasAnyParameter}>
                  <VStack spacing={1} align="flex-end">
                    <Show when={props.primaryParameter}>
                      <HStack spacing={1} align="center">
                        <Heading
                          data-testid={suffixTestId('entityHeader-primaryParameter', props)}
                          isSingleLine
                          size={3}
                        >
                          {props.primaryParameter}
                        </Heading>
                        <Show
                          when={
                            props.resourceId === 'CUSTOMER' &&
                            props.primaryParameter !==
                              i18n.t('entity.customer.labels.noPhoneNumber')
                          }
                        >
                          <Tooltip label={smsTooltipLabel} placement="top-end">
                            <IconButton
                              data-testid="open-send-message-modal"
                              icon="communication/textsms"
                              isDisabled={isSmsActionDisabled}
                              severity="informational"
                              onClick={() =>
                                openDialog(
                                  <SendCustomerSmsForm
                                    customerId={props.recordId}
                                    recordId={props.recordId}
                                    resourceId={props.resourceId}
                                  />,
                                  {
                                    scrollBehavior: 'outside',
                                    size: 'large',
                                    title: i18n.t('general.labels.smsMessage'),
                                  }
                                )
                              }
                            />
                          </Tooltip>
                        </Show>
                        <Show when={isNotNilOrEmpty(props.tertiaryParameter)}>
                          <Heading
                            data-testid={suffixTestId('entityHeader-tertiaryParameter', props)}
                            isSingleLine
                            size={3}
                          >
                            {`/ ${props.tertiaryParameter ?? ''}`}
                          </Heading>
                        </Show>
                      </HStack>
                    </Show>
                    <Show when={props.secondaryParameter}>
                      <HStack spacing={1} align="center">
                        <Text
                          color="secondary"
                          data-testid={suffixTestId('entityHeader-secondaryParameter', props)}
                          overflowWrap="anywhere"
                          size={['xSmall', 'xSmall', 'base']}
                        >
                          {props.secondaryParameter}
                        </Text>
                        <Show when={props.resourceId === 'CUSTOMER'}>
                          <InvalidEmailWarning
                            email={props.secondaryParameter}
                            data-testid={suffixTestId('entityHeader-secondaryParameter', props)}
                          />
                        </Show>
                        <Show
                          when={
                            props.resourceId === 'CUSTOMER' &&
                            props.secondaryParameter !== i18n.t('entity.customer.labels.noEmail')
                          }
                        >
                          <Tooltip
                            label={
                              isEmailConfigEnabled
                                ? i18n.t('general.actions.sendEmail')
                                : i18n.t('general.notifications.setupEmailProvider')
                            }
                            placement="top-end"
                          >
                            <IconButton
                              data-testid="open-send-email-modal"
                              icon="communication/email"
                              isDisabled={!isEmailConfigEnabled}
                              severity="informational"
                              onClick={() =>
                                openDialog(
                                  <SendCustomerEmailForm
                                    customerId={props.recordId}
                                    recordId={props.recordId}
                                    resourceId={props.resourceId}
                                  />,
                                  {
                                    withAdditionalFooter: true,
                                    size: 'large',
                                    title: i18n.t('general.actions.sendEmail'),
                                  }
                                )
                              }
                            />
                          </Tooltip>
                        </Show>
                      </HStack>
                    </Show>
                  </VStack>
                </Show>
              </HStack>
            </Stack>
          </Stack>

          <Hide onMobile onTablet>
            <Show when={props.snippets}>
              <Space vertical={4} />
              <SnippetPanel
                areSnippetsLoading={props.isLoadingSnippets}
                data-testid={suffixTestId('entityHeader-snippets', props)}
                resourceId={props.resourceId}
                snippets={props.snippets}
              />
            </Show>
          </Hide>
        </VStack>
      </Box>

      <Show when={props.alerts}>
        <VStack spacing={1}>
          {props.alerts?.map((alert, index) => (
            <Alert
              key={`${alert.variant}-${alert.title}-${alert.message}`}
              {...alert}
              data-testid={suffixTestId(`entityHeader-alert-[${index}]`, props)}
            />
          ))}
        </VStack>
      </Show>
    </>
  );
}
